"use client"

import type React from "react"
import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react"

export type ScrollableListProps = {
  children: React.ReactNode
  className?: string
  maxHeight?: number
  onDragStateChange?: (isDragging: boolean) => void
}

export type ScrollableListRef = {
  scrollToElement: (selector: string) => void
}

const ScrollableList = forwardRef<ScrollableListRef, ScrollableListProps>(
  ({ children, className = "", maxHeight = 200, onDragStateChange }, ref) => {
    const [isDragging, setIsDragging] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const dragStateRef = useRef({
      isDragging: false,
      startY: 0,
      startScrollTop: 0,
      lastY: 0,
      lastTime: 0,
    })

    useEffect(() => {
      onDragStateChange?.(isDragging)
    }, [isDragging, onDragStateChange])

    useImperativeHandle(ref, () => ({
      scrollToElement: (selector: string) => {
        const container = containerRef.current
        const content = contentRef.current
        if (!container || !content) return

        const element = content.querySelector(selector) as HTMLElement
        if (!element) return

        const containerHeight = container.clientHeight
        const elementTop = element.offsetTop
        const elementHeight = element.offsetHeight
        const currentScroll = container.scrollTop

        // Calculate if element is visible
        const elementBottom = elementTop + elementHeight
        const visibleTop = currentScroll
        const visibleBottom = currentScroll + containerHeight

        if (elementTop < visibleTop || elementBottom > visibleBottom) {
          // Center the element in the view
          const targetScroll = elementTop - (containerHeight - elementHeight) / 2
          const maxScroll = content.scrollHeight - containerHeight
          const clampedScroll = Math.max(0, Math.min(maxScroll, targetScroll))

          container.scrollTo({
            top: clampedScroll,
            behavior: "smooth",
          })
        }
      },
    }))

    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      const handleMouseDown = (e: MouseEvent) => {
        dragStateRef.current = {
          isDragging: false, // Start as false, only set to true when actual dragging occurs
          startY: e.clientY,
          startScrollTop: container.scrollTop,
          lastY: e.clientY,
          lastTime: Date.now(),
        }
        document.addEventListener("mousemove", handleMouseMove)
        document.addEventListener("mouseup", handleMouseUp)
      }

      const handleMouseMove = (e: MouseEvent) => {
        const deltaY = Math.abs(e.clientY - dragStateRef.current.startY)

        if (deltaY > 3 && !dragStateRef.current.isDragging) {
          dragStateRef.current.isDragging = true
          setIsDragging(true)
          e.preventDefault()
        }

        if (!dragStateRef.current.isDragging) return;
        e.preventDefault();

        const totalDeltaY = e.clientY - dragStateRef.current.startY;
        const newScrollTop = dragStateRef.current.startScrollTop - totalDeltaY;
        const maxScroll = container.scrollHeight - container.clientHeight;

        container.scrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));
        dragStateRef.current.lastY = e.clientY
        dragStateRef.current.lastTime = Date.now()
      }

      const handleMouseUp = (e: MouseEvent) => {
        if (dragStateRef.current.isDragging) {
          e.preventDefault()
          e.stopPropagation()
        }

        dragStateRef.current.isDragging = false
        setIsDragging(false)
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }

      const handleTouchStart = (e: TouchEvent) => {
        const touch = e.touches[0]
        if (!touch) return

        dragStateRef.current = {
          isDragging: false,
          startY: touch.clientY,
          startScrollTop: container.scrollTop,
          lastY: touch.clientY,
          lastTime: Date.now(),
        }
        setIsDragging(false)
      }

      const handleTouchMove = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;

        const deltaY = Math.abs(touch.clientY - dragStateRef.current.startY);

        // ✅ Only activate dragging AFTER threshold
        if (deltaY > 5 && !dragStateRef.current.isDragging) {
          dragStateRef.current.isDragging = true;
          setIsDragging(true);
        }

        if (!dragStateRef.current.isDragging) return;

        e.preventDefault();

        const totalDeltaY = touch.clientY - dragStateRef.current.startY;
        const newScrollTop = dragStateRef.current.startScrollTop - totalDeltaY;
        const maxScroll = container.scrollHeight - container.clientHeight;

        container.scrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));
        dragStateRef.current.lastY = touch.clientY;
        dragStateRef.current.lastTime = Date.now();
      }

      const handleTouchEnd = () => {
        if (!dragStateRef.current.isDragging) return;

        dragStateRef.current.isDragging = false;
        setIsDragging(false);
      }

      const handleWheel = (e: WheelEvent) => {
        e.preventDefault()
        const deltaY = e.deltaY * 0.5
        const newScrollTop = container.scrollTop + deltaY
        const maxScroll = container.scrollHeight - container.clientHeight
        const clampedScroll = Math.max(0, Math.min(maxScroll, newScrollTop))

        container.scrollTo({
          top: clampedScroll,
          behavior: "auto",
        })
      }

      container.addEventListener("mousedown", handleMouseDown)
      container.addEventListener("touchstart", handleTouchStart, { passive: false })
      container.addEventListener("touchmove", handleTouchMove, { passive: false })
      container.addEventListener("touchend", handleTouchEnd)
      container.addEventListener("wheel", handleWheel, { passive: false })

      return () => {
        container.removeEventListener("mousedown", handleMouseDown)
        container.removeEventListener("touchstart", handleTouchStart)
        container.removeEventListener("touchmove", handleTouchMove)
        container.removeEventListener("touchend", handleTouchEnd)
        container.removeEventListener("wheel", handleWheel)
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }
    }, [])

    return (
      <div
        ref={containerRef}
        className={`relative overflow-hidden ${className}`}
        style={{
          maxHeight: `${maxHeight}px`,
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
        data-is-dragging={isDragging}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    )
  },
)

ScrollableList.displayName = "ScrollableList"
export { ScrollableList }
